/**
 * sl-ht07.5: Attachment Size Dialog Hook
 *
 * Shows a dialog before snapshot export listing attachment sizes per database.
 * User can select/deselect databases to include/exclude their attachments.
 */

import { useConfirmModal } from '@affine/component';
import { Checkbox } from '@affine/component/ui/checkbox';
import { WorkspaceService } from '@affine/core/modules/workspace';
import type { Store } from '@blocksuite/affine/store';
import {
  collectExcludedBlobIds,
  type DatabaseAttachmentInfo,
  resolveBlobSizes,
  scanDatabaseAttachments,
} from '@blocksuite/affine/widgets/linked-doc';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useService } from '@toeverything/infra';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

// ─── Checkbox List Component ─────────────────────────────────────────────────

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 0',
};

const labelStyle: CSSProperties = {
  flex: 1,
  cursor: 'pointer',
  fontSize: 14,
};

const metaStyle: CSSProperties = {
  fontSize: 13,
  color: cssVarV2.text.secondary,
  whiteSpace: 'nowrap',
};

const summaryStyle: CSSProperties = {
  fontSize: 13,
  color: cssVarV2.text.secondary,
  paddingBottom: 8,
  borderBottom: `1px solid ${cssVarV2.layer.insideBorder.border}`,
  marginBottom: 4,
};

const quickActionsStyle: CSSProperties = {
  display: 'flex',
  gap: 12,
  paddingTop: 8,
  fontSize: 13,
};

const linkStyle: CSSProperties = {
  cursor: 'pointer',
  color: cssVarV2.text.link,
  textDecoration: 'underline',
};

function AttachmentCheckboxList({
  infos,
  onSelectionChange,
}: {
  infos: DatabaseAttachmentInfo[];
  onSelectionChange: (excludedDbIds: Set<string>) => void;
}) {
  const [checked, setChecked] = useState<Set<string>>(
    () => new Set(infos.map(i => i.databaseId))
  );

  const onChangeRef = useRef(onSelectionChange);
  onChangeRef.current = onSelectionChange;

  useEffect(() => {
    const allIds = new Set(infos.map(i => i.databaseId));
    const excluded = new Set<string>();
    for (const id of allIds) {
      if (!checked.has(id)) excluded.add(id);
    }
    onChangeRef.current(excluded);
  }, [checked, infos]);

  const toggleDb = useCallback((dbId: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(dbId)) next.delete(dbId);
      else next.add(dbId);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setChecked(new Set(infos.map(i => i.databaseId)));
  }, [infos]);

  const selectNone = useCallback(() => {
    setChecked(new Set());
  }, []);

  const totalSize = infos.reduce(
    (s: number, i: DatabaseAttachmentInfo) => s + i.totalSize,
    0
  );
  const totalFiles = infos.reduce(
    (s: number, i: DatabaseAttachmentInfo) => s + i.fileCount,
    0
  );

  return (
    <div>
      <div style={summaryStyle}>
        Total: {totalFiles} files, {formatBytes(totalSize)}
      </div>
      {infos.map(info => (
        <div key={info.databaseId} style={rowStyle}>
          <Checkbox
            checked={checked.has(info.databaseId)}
            onChange={() => toggleDb(info.databaseId)}
          />
          <span
            style={labelStyle}
            onClick={() => toggleDb(info.databaseId)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') toggleDb(info.databaseId);
            }}
          >
            {info.databaseTitle}
          </span>
          <span style={metaStyle}>
            {info.fileCount} files, {formatBytes(info.totalSize)}
          </span>
        </div>
      ))}
      <div style={quickActionsStyle}>
        <span style={linkStyle} onClick={selectAll} role="button" tabIndex={0}>
          All
        </span>
        <span style={linkStyle} onClick={selectNone} role="button" tabIndex={0}>
          None
        </span>
      </div>
    </div>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

interface AttachmentDialogResult {
  excludeBlobIds: Set<string>;
}

/**
 * Returns a function that scans docs for attachments and shows a dialog.
 * Resolves with excludeBlobIds (empty set = include all).
 */
export function useAttachmentSizeDialog() {
  const workspace = useService(WorkspaceService).workspace;
  const { openConfirmModal } = useConfirmModal();

  const showDialog = useCallback(
    (stores: Store[], onResult: (result: AttachmentDialogResult) => void) => {
      const infos = scanDatabaseAttachments(stores);
      if (infos.length === 0) {
        // No attachments — proceed without dialog
        onResult({ excludeBlobIds: new Set() });
        return;
      }

      // Resolve blob sizes, then show dialog
      const blobSync = workspace.docCollection.blobSync;
      void resolveBlobSizes(infos, async (blobId: string) => {
        const blob = await blobSync.get(blobId);
        return blob?.size ?? 0;
      }).then(() => {
        // Track user selection via ref (updated by checkbox component)
        let currentExcluded = new Set<string>();

        openConfirmModal({
          title: 'Include Attachments?',
          description:
            'Select which database attachments to include in the export.',
          confirmText: 'Export Selected',
          cancelText: 'Export Without Attachments',
          children: (
            <AttachmentCheckboxList
              infos={infos}
              onSelectionChange={excluded => {
                currentExcluded = excluded;
              }}
            />
          ),
          onConfirm: () => {
            onResult({
              excludeBlobIds: collectExcludedBlobIds(infos, currentExcluded),
            });
          },
          onCancel: () => {
            // Exclude ALL attachment blobs
            const allDbIds = new Set(
              infos.map((i: DatabaseAttachmentInfo) => i.databaseId)
            );
            onResult({
              excludeBlobIds: collectExcludedBlobIds(infos, allDbIds),
            });
          },
        });
      });
    },
    [workspace, openConfirmModal]
  );

  return showDialog;
}
